import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Package, Building2, Settings, Store, Inbox, Send, Eye, Download } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { useEntityLabels } from '../hooks/useEntityLabels.js';
import { getDashboardStats, ApiError } from '../api/client.js';
import type { DashboardStats } from '../api/client.js';
import { QuoteStatusBadge } from '../components/QuoteStatusBadge.js';

function relativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function IncomingStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    sent: { label: 'New', className: 'bg-blue-900/40 text-blue-400' },
    viewed: { label: 'Viewed', className: 'bg-gray-700 text-gray-300' },
    downloaded: { label: 'Downloaded', className: 'bg-green-900/40 text-green-400' },
  };
  const c = config[status] ?? { label: status, className: 'bg-gray-700 text-gray-400' };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const labels = useEntityLabels();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fade-in">
        <div className="rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-2xl font-semibold text-gray-100">Welcome, {user?.name}</h2>
      <p className="mt-1 mb-6 text-sm text-gray-400">
        {user?.tenant?.name}
        {user?.tenantType === 'reseller' ? ' \u2022 Reseller Account' : ''}
      </p>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="text-3xl font-bold text-gray-100">{stats?.quoteCount ?? 0}</div>
          <div className="mt-1 text-sm text-gray-400">Total Quotes</div>
          <div className="mt-2 text-xs text-gray-500">
            {stats?.draftCount ?? 0} draft &middot; {stats?.readyCount ?? 0} ready &middot;{' '}
            {stats?.finalCount ?? stats?.sentCount ?? 0} final
          </div>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="text-3xl font-bold text-gray-100">{stats?.resellerCount ?? 0}</div>
          <div className="mt-1 text-sm text-gray-400">{labels.Resellers}</div>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="text-3xl font-bold text-gray-100">{stats?.productCount ?? 0}</div>
          <div className="mt-1 text-sm text-gray-400">Products</div>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="text-3xl font-bold text-gray-100">{stats?.catalogCount ?? 0}</div>
          <div className="mt-1 text-sm text-gray-400">Catalogs</div>
        </div>
        {user?.tenantType === 'reseller' && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <div className="text-3xl font-bold text-gray-100">{stats?.incomingStats?.total ?? 0}</div>
            <div className="mt-1 text-sm text-gray-400">Incoming Quotes</div>
            {(stats?.incomingStats?.newCount ?? 0) > 0 && (
              <div className="mt-2 text-xs text-blue-400">
                {stats?.incomingStats?.newCount} new
              </div>
            )}
          </div>
        )}
        {user?.tenantType === 'manufacturer' && (stats?.sentStats?.total ?? 0) > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <div className="text-3xl font-bold text-gray-100">{stats?.sentStats?.total ?? 0}</div>
            <div className="mt-1 text-sm text-gray-400">Quotes Sent</div>
            <div className="mt-2 text-xs text-gray-500">
              {stats?.sentStats?.totalViews ?? 0} views &middot; {stats?.sentStats?.totalDownloads ?? 0} downloads
            </div>
          </div>
        )}
      </div>

      {/* Recent Quotes */}
      <div className="mb-8 rounded-lg border border-gray-800 bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-800 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-200">Recent Quotes</h3>
          <button
            onClick={() => navigate('/quotes')}
            className="text-xs text-brand-400 hover:text-brand-300"
          >
            View All &rarr;
          </button>
        </div>
        {stats?.recentQuotes && stats.recentQuotes.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3">Quote #</th>
                <th className="px-5 py-3">Project</th>
                <th className="px-5 py-3">{labels.Reseller}</th>
                <th className="px-5 py-3 text-center">Status</th>
                <th className="px-5 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentQuotes.map((q) => (
                <tr
                  key={q.id}
                  onClick={() => navigate(`/quotes/${q.id}`)}
                  className="cursor-pointer border-t border-gray-800 hover:bg-gray-800/30"
                >
                  <td className="px-5 py-3 text-sm font-semibold text-brand-400">
                    {q.displayId || '\u2014'}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-300">
                    {q.projectName || '\u2014'}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-300">
                    {q.resellerName || '\u2014'}
                  </td>
                  <td className="px-5 py-3 text-center">
                    <QuoteStatusBadge status={q.status} />
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{relativeTime(q.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-sm text-gray-500">
            No quotes yet. Create your first quote to get started.
          </div>
        )}
      </div>

      {/* Incoming Quotes — resellers only */}
      {user?.tenantType === 'reseller' && stats?.incomingStats && (
        <div className="mb-8 rounded-lg border border-gray-800 bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-800 px-5 py-4">
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-brand-400" />
              <h3 className="text-sm font-semibold text-gray-200">Incoming Quotes</h3>
              {stats.incomingStats.newCount > 0 && (
                <span className="rounded-full bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-400">
                  {stats.incomingStats.newCount} new
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/incoming-quotes')}
              className="text-xs text-brand-400 hover:text-brand-300"
            >
              View All &rarr;
            </button>
          </div>

          {stats.recentIncoming && stats.recentIncoming.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-5 py-3">From</th>
                  <th className="px-5 py-3">Project</th>
                  <th className="px-5 py-3 text-center">Status</th>
                  <th className="px-5 py-3">Received</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentIncoming.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => navigate(`/incoming-quotes/${q.id}`)}
                    className="cursor-pointer border-t border-gray-800 hover:bg-gray-800/30"
                  >
                    <td className="px-5 py-3 text-sm text-gray-300">{q.sourceTenantName}</td>
                    <td className="px-5 py-3 text-sm text-gray-300">
                      {q.projectReference || q.projectName || '\u2014'}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <IncomingStatusBadge status={q.status} />
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-500">{relativeTime(q.sentAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-sm text-gray-500">
              No incoming quotes yet.
            </div>
          )}
        </div>
      )}

      {/* Sent Quote Activity — manufacturers only */}
      {user?.tenantType === 'manufacturer' && stats?.sentStats && stats.sentStats.total > 0 && (
        <div className="mb-8 rounded-lg border border-gray-800 bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-800 px-5 py-4">
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4 text-brand-400" />
              <h3 className="text-sm font-semibold text-gray-200">Sent Quote Activity</h3>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" /> {stats.sentStats.totalViews} views
              </span>
              <span className="flex items-center gap-1">
                <Download className="h-3.5 w-3.5" /> {stats.sentStats.totalDownloads} downloads
              </span>
            </div>
          </div>

          {stats.recentSent && stats.recentSent.length > 0 && (
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-5 py-3">Reseller</th>
                  <th className="px-5 py-3">Project</th>
                  <th className="px-5 py-3 text-center">Views</th>
                  <th className="px-5 py-3 text-center">Downloads</th>
                  <th className="px-5 py-3">Sent</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentSent.map((q) => (
                  <tr key={q.id} className="border-t border-gray-800">
                    <td className="px-5 py-3 text-sm text-gray-300">{q.recipientTenantName}</td>
                    <td className="px-5 py-3 text-sm text-gray-300">
                      {q.projectReference || q.projectName || '\u2014'}
                    </td>
                    <td className="px-5 py-3 text-center text-sm text-gray-400">{q.viewCount}</td>
                    <td className="px-5 py-3 text-center text-sm text-gray-400">{q.downloadCount}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{relativeTime(q.sentAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <button
          onClick={() => navigate('/quotes/new')}
          className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:bg-gray-800"
        >
          <Plus className="h-5 w-5 text-brand-400" />
          <span className="text-sm font-medium text-gray-300">New Quote</span>
        </button>
        {user?.tenantType === 'reseller' ? (
          <button
            onClick={() => navigate('/marketplace')}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:bg-gray-800"
          >
            <Store className="h-5 w-5 text-brand-400" />
            <span className="text-sm font-medium text-gray-300">Browse Marketplace</span>
          </button>
        ) : (
          <button
            onClick={() => navigate('/catalogs')}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:bg-gray-800"
          >
            <Package className="h-5 w-5 text-brand-400" />
            <span className="text-sm font-medium text-gray-300">Add Products</span>
          </button>
        )}
        <button
          onClick={() => navigate('/resellers')}
          className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:bg-gray-800"
        >
          <Building2 className="h-5 w-5 text-brand-400" />
          <span className="text-sm font-medium text-gray-300">Manage {labels.Resellers}</span>
        </button>
        <button
          onClick={() => navigate('/settings')}
          className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-4 transition hover:bg-gray-800"
        >
          <Settings className="h-5 w-5 text-brand-400" />
          <span className="text-sm font-medium text-gray-300">Settings</span>
        </button>
      </div>
    </div>
  );
}
